/**
 * i18next, con español como idioma por defecto Y como fallback.
 *
 * Fallback en español y no en inglés a propósito: si a un catálogo le
 * faltara una clave, lo que aparece es español —el idioma del producto—,
 * no un texto en inglés que nadie pidió.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from '@tennisup/contracts';

import { CATALOGS } from './catalogs';
import { readStored, writeStored } from '../lib/storage';

export const LOCALE_STORAGE_KEY = 'tennisup.locale';

export function isSupportedLocale(value: string | null): value is SupportedLocale {
  return value !== null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * El idioma con el que arranca la pantalla: el que la persona eligió la vez
 * pasada; si no, español. Nunca el del navegador: el producto es
 * costarricense y el español es la elección por defecto explícita.
 */
export function initialLocale(): SupportedLocale {
  const stored = readStored(LOCALE_STORAGE_KEY);
  return isSupportedLocale(stored) ? stored : DEFAULT_LOCALE;
}

const resources = Object.fromEntries(
  SUPPORTED_LOCALES.map((locale) => [locale, { translation: CATALOGS[locale] }]),
);

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...SUPPORTED_LOCALES],
  // Sin esto, i18next probaría "zh" antes que "zh-CN" y no encontraría nada.
  load: 'currentOnly',
  interpolation: {
    // React ya escapa todo lo que renderiza; escapar de nuevo mostraría
    // "&#39;" en pantalla.
    escapeValue: false,
  },
  returnNull: false,
});

/** Cambia el idioma de toda la app en el acto y lo recuerda. */
export async function changeLocale(locale: SupportedLocale): Promise<void> {
  await i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
  writeStored(LOCALE_STORAGE_KEY, locale);
}

document.documentElement.lang = i18n.language;

export default i18n;
